'use client';
import cn from 'classnames';
import styles from './FilterBlock.module.scss';
import { FilterButton } from '@/atoms/FilterButton';
import { Chip } from '@/atoms/Chip';
import { SortDropdown } from '@/atoms/SortDropdown';
import {
  CategoryType,
  FetchUrlType,
  FilterFieldsType,
  InStockFilterValues,
  PageViewType,
  ProductType,
  SortFilterType,
} from '@/types';
import { useState } from 'react';
import { DesktopDrawer } from '@/atoms/DesktopDrawer';
import { useAdaptiveLayout } from '@/hooks/useAdaptiveLayout';
import { BottomDrawer } from '@/atoms/BottomDrawer';
import { ViewControls } from '@/atoms/ViewControls';
import { useCatalogFilters } from '@/hooks/useCatalogFilters';
import { getFilterDisplayValue } from '@/utils/getFilterDisplayValue';
import { useGetMoreItems } from '@/hooks/useGetMoreItems';
import { Loader } from '@/atoms/Loader';
import { ObserverLoader } from '@/atoms/ObserverLoader';
import { FilterContent } from '../FilterContent';
import { FilterProductList } from './components/FilterProductList';
import { useFilterUrl } from '@/hooks/useFilterUrl';

type FilterBlockProps = {
  className?: string;
  cards: ProductType[];
  filters: FilterFieldsType;
  pagesCount: number;
  sorts: SortFilterType[];
  type: FetchUrlType;
  categories?: CategoryType[];
  code?: string;
  hideControls?: boolean;
};

export function FilterBlock({
  className,
  cards: initialCards,
  filters,
  pagesCount,
  sorts,
  type,
  code,
  hideControls,
}: FilterBlockProps) {
  const [filterDrawerOpened, setFilterDrawerOpened] = useState<boolean>(false);
  const { isTablet, isMobile } = useAdaptiveLayout();
  const [view, setView] = useState<PageViewType>(PageViewType.Grid);
  const wrapperClassName = cn(styles.wrapper, className);

  const {
    tempSelects,
    tempRanges,
    appliedSelects,
    changeSelect,
    changeRange,
    removeFilter,
    reset,
    apply,
    changeStock,
    appliedInStock,
    tempInStock,
  } = useCatalogFilters();

  const defaultSort =
    type === FetchUrlType.Promotions
      ? (sorts.find((s) => s.code === 'relevant') ?? sorts[0])
      : sorts[0];

  const { sort, fetchUrl, handleSortChange } = useFilterUrl({
    initialSort: defaultSort,
    type,
    code,
    appliedInStock,
  });

  const {
    items: cards,
    isLoading,
    hasMore,
    handleLoadMore,
  } = useGetMoreItems({
    initialItems: initialCards,
    fetchUrl,
    pagesCount,
  });

  const toggleFilterDrawer = () => {
    setFilterDrawerOpened((prev) => !prev);
  };

  const handleViewChange = (view: PageViewType) => {
    setView(view);
  };

  const handleApplyFilters = () => {
    apply();
    toggleFilterDrawer();
  };

  const handleChangeInStock = (value: InStockFilterValues) => {
    changeStock(value);
  };

  const handleDeleteFilter = (code: string, value: string) => {
    removeFilter(code, value);
  };

  const activeFiltersCount = Object.values(appliedSelects).reduce(
    (acc, { values }) => acc + values.length,
    0,
  );

  const activeFilterChips = Object.entries(appliedSelects).map(
    ([code, { values }]) =>
      values.map((value) => {
        const { name, value: displayValue } = getFilterDisplayValue(
          code,
          value,
          filters,
        );

        return (
          <Chip
            key={`${code}-${value}`}
            onDelete={() => handleDeleteFilter(code, value)}
            text={`${name}: ${displayValue}`}
          />
        );
      }),
  );

  const filterContentProps = {
    filters,
    toggleDrawer: toggleFilterDrawer,
    onApply: handleApplyFilters,
    selects: tempSelects,
    onChangeSelect: changeSelect,
    onChangeRange: changeRange,
    onReset: reset,
    ranges: tempRanges,
    inStock: tempInStock,
    onChangeStock: handleChangeInStock,
    sorts,
    activeSort: sort,
    onChangeSort: handleSortChange,
  };

  return (
    <div className={wrapperClassName}>
      {!hideControls && (
        <>
          <div className={styles.heading}>
            <div className={styles.filters}>
              <FilterButton
                activeCount={activeFiltersCount}
                onClick={toggleFilterDrawer}
              />
              <div className={styles.active}>{activeFilterChips}</div>
            </div>
            <ViewControls
              view={view}
              onClick={handleViewChange}
              className={styles.viewControls}
            />
            {sorts.length > 0 && (
              <SortDropdown
                variants={sorts}
                value={sort}
                onChange={handleSortChange}
                className={styles.sortDropdown}
              />
            )}
          </div>
          <div className={styles.activeMob}>{activeFilterChips}</div>
        </>
      )}

      <FilterProductList
        view={view}
        isMobile={isMobile}
        cards={cards}
        type={type}
      />

      <DesktopDrawer
        active={filterDrawerOpened && !isTablet}
        onClose={toggleFilterDrawer}
      >
        <FilterContent {...filterContentProps} />
      </DesktopDrawer>
      <BottomDrawer
        active={filterDrawerOpened && !!isTablet}
        onClose={toggleFilterDrawer}
      >
        <FilterContent {...filterContentProps} />
      </BottomDrawer>
      {isLoading && <Loader />}
      {!isLoading && (
        <ObserverLoader
          onIntersect={handleLoadMore}
          isLoading={isLoading}
          hasMore={hasMore}
        />
      )}
    </div>
  );
}
